import { useEffect, useRef, useCallback } from "react"
import { SHORT_CONFIG } from "constants/short"

export default function useShortScroll({
  listShort,
  currentIndex,
  setCurrentIndex,
  setIsPlaying,
  scrollRef,
  videoContainersRef
}) {
  const isScrollingRef = useRef(false)
  const scrollTimeoutRef = useRef(null)
  const lastScrollTime = useRef(0)

  // Scroll to specific index
  const scrollToIndex = useCallback(
    (index) => {
      if (!scrollRef.current || index < 0 || index >= listShort.length) return

      const targetEl = videoContainersRef.current[index]
      if (!targetEl) return

      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }

      isScrollingRef.current = true
      lastScrollTime.current = Date.now()

      // Smooth scroll to target
      scrollRef.current.scrollTo({
        top: targetEl.offsetTop,
        behavior: "smooth"
      })

      // Update state immediately for better UX
      setCurrentIndex(index)
      setIsPlaying(true)

      // Reset scrolling flag after animation
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false
      }, SHORT_CONFIG.SCROLL_ANIMATION_DURATION)
    },
    [listShort.length, scrollRef, videoContainersRef, setCurrentIndex, setIsPlaying]
  )

  // Navigate functions
  const scrollToNext = useCallback(() => {
    if (currentIndex < listShort.length - 1) {
      scrollToIndex(currentIndex + 1)
    }
  }, [currentIndex, listShort.length, scrollToIndex])

  const scrollToPrev = useCallback(() => {
    if (currentIndex > 0) {
      scrollToIndex(currentIndex - 1)
    }
  }, [currentIndex, scrollToIndex])

  // IntersectionObserver for detecting visible video
  useEffect(() => {
    if (!listShort.length || !videoContainersRef.current.length) return

    const observer = new IntersectionObserver(
      (entries) => {
        // Ignore during programmatic scroll
        if (isScrollingRef.current) return

        // Prevent rapid updates
        const now = Date.now()
        if (now - lastScrollTime.current < SHORT_CONFIG.SCROLL_DEBOUNCE) return

        let maxRatio = 0
        let visibleIndex = -1

        entries.forEach((entry) => {
          const index = videoContainersRef.current.findIndex((el) => el === entry.target)

          // Find the most visible video
          if (entry.intersectionRatio > maxRatio && entry.intersectionRatio > 0.5) {
            maxRatio = entry.intersectionRatio
            visibleIndex = index
          }
        })

        // Update current index if changed
        if (visibleIndex !== -1 && visibleIndex !== currentIndex) {
          setCurrentIndex(visibleIndex)
          setIsPlaying(true)
        }
      },
      {
        root: scrollRef.current,
        threshold: SHORT_CONFIG.INTERSECTION_THRESHOLDS,
        rootMargin: "0px"
      }
    )

    // Observe all video containers
    videoContainersRef.current.forEach((el) => {
      if (el) observer.observe(el)
    })

    return () => {
      observer.disconnect()
    }
  }, [listShort.length, currentIndex, scrollRef, videoContainersRef, setCurrentIndex, setIsPlaying])

  // Scroll to initial index on mount
  useEffect(() => {
    if (listShort.length > 0 && currentIndex === 0) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        scrollToIndex(0)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [listShort.length])

  // Cleanup
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  return {
    scrollToIndex,
    scrollToNext,
    scrollToPrev
  }
}
